package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.IdentityData
import com.neurobs.ccabserver.administrator.contexts.{MenuContext, ResourceURLContext, ServerContext}
import com.neurobs.errors._
import com.neurobs.react.{StyleUtil, includeAllIf, includeIf}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.ReactElement
import slinky.web.html.{alt, className, div, hr, i, img, key, onClick, span, src, style}

import scala.concurrent.ExecutionContext.Implicits.global

object NavFramework {

	case class IconData
	(
		content: Option[String] = None,
		className: Option[String] = None,
		sizeScaleFactor: Option[Double] = None
	)

	@react object Icon {
		case class Props
		(
			data: IconData
		)

		val component = FunctionalComponent[Props] { props =>
			val icon = props.data
			icon.content match {
				case Some(c) =>
					span(
						className := icon.className.getOrElse(""),
						style := StyleUtil.makeStyle(("fontSize", s"${icon.sizeScaleFactor.getOrElse(1)}em"))
					)(c)
				case None =>
					i(
						className := icon.className.getOrElse(""),
						style := StyleUtil.makeStyle(("fontSize", s"${icon.sizeScaleFactor.getOrElse(1)}em"))
					)
			}
		}
	}

	object ItemType extends Enumeration {
		val Alert = Value("Alert")
		val Item = Value("Item")
	}

	case class MenuItemData
	(
		itemType: ItemType.Value,
		icon: IconData,
		caption: String
	)

	case class MenuSectionData
	(
		items: Seq[MenuItemData]
	)

	@react object MenuItem {
		case class Props
		(
			item: MenuItemData,
			selected: Boolean,
			onSelection: String => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val captionClass = if (props.selected || (props.item.itemType == ItemType.Alert)) "nfw-menu-item-caption-selected" else "nfw-menu-item-caption"
			val iconClass = if (props.item.itemType == ItemType.Alert) "nfw-menu-item-icon nfw-menu-item-icon-alert" else if (props.selected) "nfw-menu-item-icon nfw-menu-item-icon-selected" else "nfw-menu-item-icon"

			div(className := "nfw-menu-item", onClick := {() => props.onSelection(props.item.caption)})(
				div(className := iconClass)(
					Icon(data = props.item.icon)
				),
				span(className := captionClass)(props.item.caption)
			)
		}
	}

	@react object Menu {
		case class Props
		(
			sections: Seq[MenuSectionData],
			alerts: Seq[String],
			selections: Seq[String],
			onSelection: String => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val allItems: Seq[Option[MenuItemData]] = props.sections.zipWithIndex.flatMap { s =>
				val filtered = s._1.items.filter(i => (i.itemType == ItemType.Item) || (props.alerts.contains(i.caption)))
				if (filtered.isEmpty) Seq()
				else filtered.map(Some(_)) ++ (if (s._2 < props.sections.length - 1) Seq(None) else Seq())
			}

			var lineCounter = 0
			div(className := "nfw-menu")(
				allItems map {
					case None =>
						hr(className := "nfw-fade-line", key := s"line ${lineCounter += 1; lineCounter}") : ReactElement
					case Some(i) =>
						MenuItem(
							item = i,
							selected = props.selections.contains(i.caption),
							onSelection = props.onSelection
						).withKey(i.caption) : ReactElement
				}
			)
		}
	}

	/** A profile button and a pop-up menu underneath toggled by the button */
	@react object ProfileMenu {
		case class Props
		(
			user: IdentityData,
			onLogoutClicked: () => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val server = ServerContext.accessState()
			val (profileMenuVisible, setProfileMenuVisible) = useState[Boolean](default = false)
			val (displayName, setDisplayName) = useState[String](default = "")
			val userDisplay = props.user.userDisplay

			useEffect({ () =>
				server.getAccount(accountId = props.user.legacyAccountId)._2 map {
					case Left(_) =>
					case Right(a) => setDisplayName(a.organization)
				}
			}, Seq())

			div(className := "nfw-profile-container")(
				div(
					className := "nfw-header-button",
					onClick := {() => setProfileMenuVisible(!profileMenuVisible)}
				)(i(className := "fa-solid fa-user")),
				toOption(profileMenuVisible)(
					div(className := "nfw-profile-menu-container")(
						div(className := "nfw-profile-menu")(
							div(className := "nfw-profile-menu-header")("Account"),
							div(className := "nfw-profile-menu-text")(displayName),
							div(className := "nfw-profile-menu-header")("User"),
							div(className := "nfw-profile-menu-text")(userDisplay),
							div(
								className := "nfw-profile-menu-logout",
								onClick := props.onLogoutClicked
							)(i(className := "fa-solid fa-arrow-right-from-bracket"), " Logout")
						)
					)
				)
			)
		}
	}

	/** Bar that contains the logo, menu button, alerts, and profile menu */
	@react object HeaderBar {
		case class Props
		(
			alertItems: Seq[MenuItemData],
			user: Option[IdentityData],
			alertCaptions: Seq[String],
			onMenuButtonClicked: () => Unit,
			onLogoutClicked: () => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val resources = ResourceURLContext.accessState()
			div(className := "nfw-header")(
				div(className := "nfw-header-left")(
					div(className := "nfw-header-space"),
					img(className := "nfw-logo", src := resources.get(ResourceURLContext.Resource.HorizontalLogo), alt := "Logo"),
					includeAllIf(props.user.isDefined)(
						div(className := "nfw-header-space", key := "s1"),
						div(className := "nfw-header-space", key := "s2"),
						div(className := "nfw-header-space", key := "s3"),
						div(className := "nfw-header-button", onClick := props.onMenuButtonClicked, key := "b")(
							i(className := "fa-solid fa-bars")
						)
					)
				),
				div(className := "nfw-header-right")(
					includeAllIf(props.user.isDefined)(
						props.alertItems.filter(i => props.alertCaptions.contains(i.caption)).map { i =>
							div(className := "nfw-inline_container", key := i.caption)(
								div(className := "nfw-header-button nfw-header-button-alert")(
									Icon(data = i.icon)
								),
								div(className := "nfw-header-space")
							)
						},
						props.user map { u =>
							ProfileMenu(user = u, onLogoutClicked = props.onLogoutClicked).withKey("m")
						}
					)
				)
			)
		}
	}

	@react object View {
		case class Props
		(
			sections: Seq[MenuSectionData],
			user: Option[IdentityData],
			alerts: Seq[String],
			selections: Seq[String],
			onLogoutClicked: () => Unit,
			onSelection: String => Unit,
			content: ReactElement
		)

		val component = FunctionalComponent[Props] { props =>
			val context = MenuContext.accessState()

			div(className := "nfw-root")(
				HeaderBar(
					alertItems = props.sections.flatMap(_.items.filter(_.itemType == ItemType.Alert)),
					user = props.user,
					alertCaptions = props.alerts,
					onMenuButtonClicked = {() => context.setMenuVisible(!context.value.menuVisible)},
					onLogoutClicked = props.onLogoutClicked
				),
				div(className := "nfw-main")(
					div(className := "nfw-main-left")(
						includeIf(props.user.isDefined && context.value.menuVisible)(
							Menu(
								sections = props.sections,
								alerts = props.alerts,
								selections = props.selections,
								onSelection = props.onSelection
							).withKey("m")
						)
					),
					div(className := "nfw-main-right")(props.content)
				)
			)
		}
	}
}
